import React, { useState, useEffect } from 'react';
import { getIndustries, type Industry } from '../../../api/comparisonApi';

const INDUSTRY_ICONS: Record<string, string> = {
  technology: '💻',
  tech: '💻',
  saas: '☁️',
  finance: '🏦',
  healthcare: '🏥',
  'real-estate': '🏠',
  'real_estate': '🏠',
  agency: '📣',
  'marketing-agency': '📣',
  'marketing_agency': '📣',
  ecommerce: '🛒',
  ecom: '🛒',
  construction: '🏗️',
  'plumbing-hvac': '🔧',
  plumbing_hvac: '🔧',
  plumbing: '🔧',
  hvac: '🔧',
  roofing: '🏚️',
  electrical: '⚡',
  landscaping: '🌿',
  cleaning: '✨',
  'auto-repair': '🚗',
  auto_repair: '🚗',
  automotive: '🚗',
  retail: '🏪',
  education: '📚',
  hospitality: '🏨',
  manufacturing: '🏭',
  services: '💼',
  'professional-services': '💼',
  professional_services: '💼',
  legal: '⚖️',
  accounting: '🧾',
  insurance: '🛡️',
  logistics: '🚚',
  food: '🍽️',
  restaurant: '🍽️',
  fitness: '💪',
  beauty: '💅',
  other: '•',
};

interface IndustrySelectorProps {
  value: string | null;
  onChange: (industryId: string, industryName: string) => void;
  disabled?: boolean;
}

export const IndustrySelector: React.FC<IndustrySelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [filteredIndustries, setFilteredIndustries] = useState<Industry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadIndustries = async () => {
      try {
        setLoading(true);
        const list = await getIndustries();
        setIndustries(list);
        setFilteredIndustries(list);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };
    loadIndustries();
  }, []);

  useEffect(() => {
    const filtered = industries.filter((industry) =>
      industry.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredIndustries(filtered);
  }, [searchTerm, industries]);

  if (loading) return <p className="text-sm text-gray-500">Loading industries...</p>;
  if (error) return <p className="text-sm text-red-600">Error: {error}</p>;

  return (
    <div>
      <input
        type="text"
        placeholder="Search industries…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={disabled}
        className="w-full max-w-md px-4 py-3 rounded-xl border border-gray-200 text-sm text-secondary outline-none mb-5 bg-white transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
      />

      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
      >
        {filteredIndustries.map((industry) => {
          const active = value === industry.id;
          return (
            <button
              key={industry.id}
              onClick={() => !disabled && onChange(industry.id, industry.name)}
              disabled={disabled}
              className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-left text-sm font-medium transition-all ${
                active
                  ? 'border-primary bg-red-50 text-primary'
                  : 'border-gray-200 bg-white text-secondary hover:border-primary hover:bg-red-50/50'
              }`}
              style={{ fontWeight: active ? 600 : 500 }}
            >
              {(INDUSTRY_ICONS[industry.slug] || INDUSTRY_ICONS[industry.id]) && (
                <span className="text-lg leading-none">
                  {INDUSTRY_ICONS[industry.slug] || INDUSTRY_ICONS[industry.id]}
                </span>
              )}
              <span className="leading-snug">{industry.name}</span>
            </button>
          );
        })}
      </div>

      {filteredIndustries.length === 0 && (
        <p className="text-sm text-gray-500 mt-4">No industries match your search.</p>
      )}
    </div>
  );
};
